import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { bugImagesApi } from '../../api/acceptanceApi';
import { getApiErrorMessage, resolveBugImageUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { SavedImageGrid } from '../../components/SavedImageGrid';
import type { BugImage } from '../../types';

interface BugImageGalleryProps {
  bugId: string;
  /** Hiện nút xoá trên từng ảnh (cần permission BUG_UPDATE). */
  canEdit: boolean;
  /** Đổi giá trị để buộc nạp lại sau khi dialog sửa bug đóng. */
  reloadKey?: number;
}

/** Ảnh đính kèm của bug trong dialog chi tiết. */
export function BugImageGallery({ bugId, canEdit, reloadKey = 0 }: BugImageGalleryProps): JSX.Element | null {
  const [images, setImages] = useState<BugImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    bugImagesApi
      .list(bugId)
      .then((items) => {
        if (!cancelled) setImages(items);
      })
      .catch(() => {
        if (!cancelled) notify('Không tải được ảnh đính kèm', 'warning');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bugId, reloadKey]);

  const handleRemove = async (image: BugImage) => {
    try {
      await bugImagesApi.remove(image.id);
      setImages((prev) => prev.filter((i) => i.id !== image.id));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá ảnh'), 'error');
    }
  };

  // Chưa tải xong hoặc bug không có ảnh: không chiếm chỗ trong dialog.
  if (loading || images.length === 0) return null;

  return (
    <>
      <Typography variant="subtitle2" fontWeight={700}>
        Ảnh đính kèm ({images.length})
      </Typography>
      <SavedImageGrid
        images={images}
        resolveUrl={resolveBugImageUrl}
        onRemove={canEdit ? (img) => void handleRemove(img) : undefined}
      />
    </>
  );
}
